import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { adminApiClient } from '../api/client';
import { Card, CardTitle } from '../components/ui/Card';
import { StatCard } from '../components/ui/StatCard';
import { Table, TableHead, TableBody, TableRow, TableCell } from '../components/ui/Table';
import { Pagination } from '../components/ui/Pagination';
import { Badge } from '../components/ui/Badge';
import { Loading } from '../components/ui/Loading';
import { Coins, TrendingUp, Zap } from 'lucide-react';
import { formatTokens } from '../utils/format-tokens';

const TokenUsage: React.FC = () => {
  const [accountPage, setAccountPage] = useState(1);
  const pageSize = 20;

  const { data: summary, isLoading: summaryLoading } = useQuery({
    queryKey: ['token-usage-summary'],
    queryFn: () => adminApiClient.getTokenUsageSummary(),
  });

  const { data: byAccount, isLoading: byAccountLoading } = useQuery({
    queryKey: ['token-usage-by-account', accountPage, pageSize],
    queryFn: () => adminApiClient.getTokenUsageByAccount({ page: accountPage, page_size: pageSize }),
  });

  const { data: byLLM, isLoading: byLLMLoading } = useQuery({
    queryKey: ['token-usage-by-llm'],
    queryFn: () => adminApiClient.getTokenUsageByLLM(),
  });

  if (summaryLoading) {
    return <Loading message="Loading token usage..." />;
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-white">Token Usage</h1>
        <p className="text-gray-400 mt-1">
          Platform-wide token consumption and costs
        </p>
      </div>

      {/* Summary Stats */}
      {summary && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <StatCard
            title="Calculated Tokens"
            value={formatTokens(summary.total_tokens)}
            icon={Coins}
          />
          <StatCard
            title="Raw LLM Tokens"
            value={formatTokens(summary.total_external_tokens)}
            subtitle={`${formatTokens(summary.total_external_input_tokens)} in / ${formatTokens(summary.total_external_output_tokens)} out`}
            icon={Zap}
          />
          <StatCard
            title="Avg Per Analysis"
            value={formatTokens(summary.avg_per_analysis)}
            icon={TrendingUp}
          />
          <StatCard
            title="This Month"
            value={formatTokens(summary.this_month_tokens)}
            trend={{ value: summary.this_week_tokens, label: 'this week', isPositive: true }}
          />
        </div>
      )}

      {/* Token Distribution */}
      {summary && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <Card>
            <CardTitle className="mb-4">Calculated Tokens</CardTitle>
            <div className="space-y-4">
              <TokenBar
                label="Input Tokens"
                value={summary.total_input_tokens}
                total={summary.total_tokens}
                color="bg-blue-500"
              />
              <TokenBar
                label="Output Tokens"
                value={summary.total_output_tokens}
                total={summary.total_tokens}
                color="bg-green-500"
              />
            </div>
            <div className="mt-4 pt-4 border-t border-gray-700">
              <CardTitle className="mb-4 text-sm">Raw LLM Tokens</CardTitle>
              <div className="space-y-4">
                <TokenBar
                  label="Raw Input"
                  value={summary.total_external_input_tokens}
                  total={summary.total_external_tokens}
                  color="bg-blue-400/70"
                />
                <TokenBar
                  label="Raw Output"
                  value={summary.total_external_output_tokens}
                  total={summary.total_external_tokens}
                  color="bg-green-400/70"
                />
              </div>
            </div>
          </Card>

          <Card>
            <CardTitle className="mb-4">Time Period Breakdown</CardTitle>
            <div className="grid grid-cols-3 gap-4 text-center">
              <div className="p-4 bg-gray-900 rounded-lg">
                <p className="text-2xl font-semibold text-white">
                  {formatTokens(summary.today_tokens)}
                </p>
                <p className="text-sm text-gray-400 mt-1">Today</p>
              </div>
              <div className="p-4 bg-gray-900 rounded-lg">
                <p className="text-2xl font-semibold text-white">
                  {formatTokens(summary.this_week_tokens)}
                </p>
                <p className="text-sm text-gray-400 mt-1">This Week</p>
              </div>
              <div className="p-4 bg-gray-900 rounded-lg">
                <p className="text-2xl font-semibold text-white">
                  {formatTokens(summary.this_month_tokens)}
                </p>
                <p className="text-sm text-gray-400 mt-1">This Month</p>
              </div>
            </div>
          </Card>
        </div>
      )}

      {/* Usage by LLM */}
      <Card padding="none">
        <div className="p-6 border-b border-gray-700">
          <CardTitle>Usage by LLM Model</CardTitle>
        </div>

        {byLLMLoading ? (
          <div className="p-6">
            <Loading message="Loading LLM usage..." />
          </div>
        ) : byLLM && byLLM.data.length > 0 ? (
          <Table>
            <TableHead>
              <TableRow>
                <TableCell header>LLM Agent</TableCell>
                <TableCell header>Provider</TableCell>
                <TableCell header>Model</TableCell>
                <TableCell header>Calculated Tokens</TableCell>
                <TableCell header>Raw Tokens</TableCell>
                <TableCell header>Cost</TableCell>
                <TableCell header>Usage Count</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {byLLM.data.map((item, index) => (
                <TableRow key={index}>
                  <TableCell>
                    <span className="font-medium text-white">{item.llm_agent}</span>
                  </TableCell>
                  <TableCell>
                    <Badge variant="info">{item.llm_provider}</Badge>
                  </TableCell>
                  <TableCell>
                    <span className="text-gray-400 text-xs font-mono">{item.llm_model}</span>
                  </TableCell>
                  <TableCell>
                    <div>
                      <span className="text-white">{formatTokens(item.total_tokens)}</span>
                      <div className="text-xs text-gray-500">
                        {formatTokens(item.total_input)} in / {formatTokens(item.total_output)} out
                      </div>
                    </div>
                  </TableCell>
                  <TableCell>
                    <div>
                      <span className="text-yellow-300">{formatTokens(item.external_tokens)}</span>
                      <div className="text-xs text-gray-500">
                        {formatTokens(item.external_input)} in / {formatTokens(item.external_output)} out
                      </div>
                    </div>
                  </TableCell>
                  <TableCell>
                    <span className="text-green-400">${(item.total_cost_cents / 100).toFixed(2)}</span>
                  </TableCell>
                  <TableCell>
                    <span className="text-gray-300">{item.usage_count.toLocaleString()}</span>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        ) : (
          <div className="p-12 text-center text-gray-400">
            No LLM usage data available
          </div>
        )}
      </Card>

      {/* Usage by Account */}
      <Card padding="none">
        <div className="p-6 border-b border-gray-700">
          <CardTitle>Usage by Account</CardTitle>
        </div>

        {byAccountLoading ? (
          <div className="p-6">
            <Loading message="Loading account usage..." />
          </div>
        ) : byAccount && byAccount.data.length > 0 ? (
          <>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell header>Account ID</TableCell>
                  <TableCell header>Calculated Tokens</TableCell>
                  <TableCell header>Raw Tokens</TableCell>
                  <TableCell header>Cost</TableCell>
                  <TableCell header>Analyses</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {byAccount.data.map((item) => (
                  <TableRow key={item.account_id}>
                    <TableCell>
                      <span className="font-mono text-sm text-gray-300">
                        {item.account_id.slice(0, 12)}...
                      </span>
                    </TableCell>
                    <TableCell>
                      <div>
                        <span className="text-white font-medium">{formatTokens(item.total_tokens)}</span>
                        <div className="text-xs text-gray-500">
                          {formatTokens(item.total_input)} in / {formatTokens(item.total_output)} out
                        </div>
                      </div>
                    </TableCell>
                    <TableCell>
                      <div>
                        <span className="text-yellow-300">{formatTokens(item.external_tokens)}</span>
                        <div className="text-xs text-gray-500">
                          {formatTokens(item.external_input)} in / {formatTokens(item.external_output)} out
                        </div>
                      </div>
                    </TableCell>
                    <TableCell>
                      <span className="text-green-400">${(item.total_cost_cents / 100).toFixed(2)}</span>
                    </TableCell>
                    <TableCell>
                      <span className="text-gray-300">{item.analysis_count}</span>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>

            <Pagination
              currentPage={byAccount.page}
              totalPages={byAccount.total_pages}
              totalItems={byAccount.total}
              pageSize={pageSize}
              onPageChange={setAccountPage}
            />
          </>
        ) : (
          <div className="p-12 text-center text-gray-400">
            No account usage data available
          </div>
        )}
      </Card>
    </div>
  );
};

function TokenBar({ label, value, total, color }: {
  label: string;
  value: number;
  total: number;
  color: string;
}) {
  const pct = total > 0 ? (value / total) * 100 : 0;
  return (
    <div>
      <div className="flex justify-between mb-2">
        <span className="text-gray-400">{label}</span>
        <span className="text-white">{formatTokens(value)}</span>
      </div>
      <div className="w-full bg-gray-700 rounded-full h-3">
        <div
          className={`h-3 rounded-full ${color}`}
          style={{ width: `${pct}%` }}
        />
      </div>
    </div>
  );
}


export default TokenUsage;
